import bbox from "@turf/bbox";
import { Nullable } from "src/js/types/Nullable";
import { SearchAutocompleteSection } from "../../types";
import { getSearchAreas } from "./getSearchAreas";

export function getAutocompleteSuggestions(input: string): Promise<google.maps.places.AutocompletePrediction[]> {
  return new Promise((resolve) => {
    const autocomplete = new google.maps.places.AutocompleteService();
    const { countries: country, geometry } = getSearchAreas();
    const limitBbox = bbox(geometry);

    const bounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(limitBbox[1], limitBbox[0]),
      new google.maps.LatLng(limitBbox[3], limitBbox[2])
    );

    autocomplete.getPlacePredictions(
      {
        input,
        bounds,
        componentRestrictions: { country },
        types: ["establishment", "geocode"],
      },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(results);
        } else {
          resolve([]);
        }
      }
    );
  });
}

export function findPlaceLocation(
  placeId: string,
  onSearchLocationChange?: (location: null | { geometry: any; place: any }) => void
): Promise<Nullable<{ location: google.maps.LatLng; place: google.maps.places.PlaceResult }>> {
  return new Promise((resolve) => {
    const placesService = new google.maps.places.PlacesService(document.createElement("div"));

    placesService.getDetails({ placeId }, function (place, status) {
      const location = place?.geometry?.location;

      if (status === google.maps.places.PlacesServiceStatus.OK && location) {
        resolve({ location, place });

        // @deprecated used to place pin in old location search
        onSearchLocationChange?.({ geometry: { location }, place });
      } else {
        resolve(null);
      }
    });
  });
}

export const addressesAutocompleteSection: SearchAutocompleteSection = {
  id: "adressess",
  title: "Addresses",
  async fetchResults(search: string) {
    if (!search?.trim()) return [];

    const suggestions = await getAutocompleteSuggestions(search);

    return suggestions.map((suggestion) => ({
      id: suggestion.place_id,
      title: suggestion.description,
    }));
  },
  async onSelectionHandler({ id }, context) {
    const result = await findPlaceLocation(id, context.onSearchLocationChange);

    if (!result) throw new Error("Failed to get location");

    const { location, place } = result;

    if (place.geometry?.viewport) {
      context.map?.fitBounds(place.geometry?.viewport);
    } else {
      context.map?.setCenter(location);
    }

    return {
      lat: location.lat(),
      lng: location.lng(),
      location: place.formatted_address,
      location_shortname: place.name,
    };
  },
};
